import { Fragment, FunctionComponent, useRef } from 'react';
import { useSelector } from 'react-redux';
import cn from 'classnames';
import { Appearance, Design, PrintArea, ProductType, Shop } from '@/types';
import { t } from '@/utils/i18n';
import { getDesignImageUrl } from '@/utils/imageUrls/getDesignImageUrl';
import { printTypesForDesign } from '@/model/printType/printTypesForDesign';
import { selectDesign } from '@/redux/designs/getters/selectDesign';
import { Button } from '@/components/common/Button';
import { LoadingImage } from '@/components/common/LoadingImage';
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalHeaderBack,
  ModalTypes,
} from '@/components/common/modal/modal';
import { DesignPrice } from './DesignPrice';
import './DesignPreview.scss';

interface DesignPreviewContentProps {
  design: Design;
  appearance: Appearance;
  printArea?: PrintArea;
  productType: ProductType;
  onDesignSelect: (design: Design) => void;
  context: Shop;
  isMarketplaceCategoryActive: boolean;
}

const DesignPreviewContent: FunctionComponent<DesignPreviewContentProps> = ({
  design,
  appearance,
  printArea,
  productType,
  onDesignSelect,
  context,
  isMarketplaceCategoryActive,
}) => {
  const printable = printTypesForDesign(design, printArea, appearance).length > 0;

  const imageUrl = getDesignImageUrl(design.id, {
    width: 300,
    height: 250,
  });

  const imageClasses = cn('design-item-preview__image', {
    'design-item-preview__image--light': Boolean(design.backgroundColor),
  });

  return (
    <Fragment>
      <div className={imageClasses}>
        <LoadingImage src={imageUrl} alt={design.name} />
      </div>
      <div className="design-item-preview__info">
        <div className="design-item-preview__name-container">
          <h3 className="design-item-preview__name">{design.name}</h3>
          <DesignPrice
            className="design-item-preview__price"
            productType={productType}
            design={design}
            zeroValue=""
            zeroClassName="design-item-preview__price--hidden"
            context={context}
            isMarketplaceCategoryActive={isMarketplaceCategoryActive}
          />
        </div>
        <p className="design-item-preview__id">ID: {design.id}</p>
      </div>
      <div className="design-selector__cta-container">
        <Button
          medium
          className="design-selector__cta"
          onClick={() => onDesignSelect(design)}
          disabled={!printable}
        >
          {t('designPanel.selectProduct')}
        </Button>
      </div>
    </Fragment>
  );
};

interface DesignPreviewProps {
  designId?: string;
  onBack: () => void;
  onClose: () => void;
  appearance: Appearance;
  printArea?: PrintArea;
  productType: ProductType;
  onDesignSelect: (design: Design) => void;
  context: Shop;
  isMarketplaceCategoryActive: boolean;
}

export const DesignPreview: FunctionComponent<DesignPreviewProps> = ({
  designId = '',
  onBack,
  onClose,
  productType,
  printArea,
  appearance,
  onDesignSelect,
  context,
  isMarketplaceCategoryActive,
}) => {
  const design = useSelector(selectDesign(designId));
  const lastDesign = useRef(design);

  if (design && lastDesign.current !== design) {
    lastDesign.current = design;
  }

  return (
    <Modal type={ModalTypes.panel} open={Boolean(design)} title={design?.name} onClose={onClose}>
      <ModalHeader>
        <ModalHeaderBack onBack={onBack}>{t('modal.back', t('designPanel.title'))}</ModalHeaderBack>
      </ModalHeader>
      <ModalContent flex>
        <DesignPreviewContent
          key={lastDesign.current?.id}
          design={lastDesign.current}
          appearance={appearance}
          printArea={printArea}
          productType={productType}
          onDesignSelect={onDesignSelect}
          context={context}
          isMarketplaceCategoryActive={isMarketplaceCategoryActive}
        />
      </ModalContent>
    </Modal>
  );
};
